import { FC, useState } from "react";

import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import {
  ConnectInvitationModel,
  type PauseTarget,
  postConnectMeetingPause,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { consoleMsgFrom } from "./apiError";

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  meetingId: string;
  /** Who's currently in the room and can be paused. */
  admitted: ConnectInvitationModel[];
  onPaused: (target: PauseTarget) => void;
}

type Mode = "all" | "selected";

const PausePickerDialog: FC<Props> = ({
  open,
  onClose,
  orgId,
  meetingId,
  admitted,
  onPaused,
}) => {
  const { t } = useTranslation("connect");
  const [mode, setMode] = useState<Mode>("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setMode("all");
    setSelectedIds([]);
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const toggle = (id: string) =>
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );

  // The API only pauses the interviewer as part of "all", so a selection is
  // always others-only.
  const confirm = async () => {
    const target: PauseTarget =
      mode === "all"
        ? { kind: "all" }
        : {
            kind: "selected",
            invitation_ids: selectedIds,
            include_interviewer: false,
          };
    setBusy(true);
    try {
      await postConnectMeetingPause({
        path: { org_id: orgId, meeting_id: meetingId },
        body: { paused: true, target },
      });
      toast.success(t("pause.toast.paused"));
      onPaused(target);
      reset();
      onClose();
    } catch (err) {
      toast.error(consoleMsgFrom(err) ?? t("pause.toast.failed"));
    } finally {
      setBusy(false);
    }
  };

  const nothingSelected = mode === "selected" && selectedIds.length === 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("pause.title")}</DialogTitle>
      <DialogContent>
        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
        >
          <FormControlLabel
            value="all"
            control={<Radio />}
            label={t("pause.everyone")}
          />
          <FormControlLabel
            value="selected"
            control={<Radio />}
            label={t("pause.selectPeople")}
          />
        </RadioGroup>

        {mode === "selected" && (
          <Stack
            spacing={0.5}
            mt={1}
            sx={{ maxHeight: 280, overflowY: "auto" }}
          >
            {admitted.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("pause.noOneElse")}
              </Typography>
            ) : (
              admitted.map((i) => (
                <FormControlLabel
                  key={i.id}
                  control={
                    <Checkbox
                      checked={selectedIds.includes(i.id)}
                      onChange={() => toggle(i.id)}
                    />
                  }
                  label={`${i.name} · ${i.participant_type}`}
                />
              ))
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          {t("pause.cancel")}
        </Button>
        <Button
          onClick={() => void confirm()}
          variant="contained"
          disabled={busy || nothingSelected}
        >
          {busy ? t("pause.pausing") : t("pause.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PausePickerDialog;
